<template>
  <a-modal
    title="Edit Pay Code"
    :visible="visible"
    width="480px"
    data-testid="shift-view-edit-pay-code-modal"
    @cancel="closePayCodeModal"
  >
    <shift-overlap-modal
      :visible.sync="visibleOverlapModal"
      :overlapping-shifts="overlappingShifts"
      :loading="false"
      data-testid="shift-view-edit-pay-code-overlap-modal"
      @reload="submit"
    />

    <a-form-model
      layout="horizontal"
      :model="form"
      :rules="rules"
      class="flex"
      ref="payCodeForm"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <div class="flex flex-column gap-2">
        <a-form-model-item :colon="false" label-align="left" label="Type">
          <a-select :value="type" disabled>
            <a-select-option value="attached">Attached</a-select-option>
            <a-select-option value="independent">Independent</a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item :colon="false" label-align="left" label="Pay Code" prop="pay_code">
          <a-select v-model="payCodeProxy" placeholder="Select Pay Code">
            <a-select-option v-for="payCode in availablePayCodes" :key="payCode.id" :value="payCode.id">
              {{ payCode.name }}
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item
          v-if="form.pay_code"
          :colon="false"
          label-align="left"
          :label="`Value, ${form.pay_code.type.toLowerCase() === 'total' ? '$' : 'hours'}`"
        >
          <a-input-number v-model="form.pay_code.value" :min="0" :step="0.1" :precision="2" placeholder="Enter value" />
        </a-form-model-item>

        <a-form-model-item v-if="form.pay_code" :colon="false" label-align="left" label="Start Time" prop="time">
          <a-time-picker
            :value="timeValue"
            :allow-clear="false"
            :default-open-value="defaultTimeValue"
            format="hh:mm A"
            :use12Hours="true"
            class="w-full"
            @change="handleTimeChange"
          />
        </a-form-model-item>

        <a-form-model-item v-if="form.pay_code" :colon="false" label-align="left" label="Ready To Pay Status">
          <custom-select
            :disabled="shift.internal_payment_info.ready_for_pay_status === 'blocked'"
            :multiple="false"
            :value.sync="form.ready_for_pay_status"
            placeholder="Status"
            :allow-clear="false"
            :options="readyForPayStatus"
            name="title"
            optionKey="key"
          />
        </a-form-model-item>
      </div>
    </a-form-model>

    <pay-code-accrual-confirmation
      :visible="visibleAccrualConfirmation"
      :rows="outsideBalanceRows"
      :total-associates="1"
      :is-batch="false"
      title="Save Payable Pay Code Without Enough Balance?"
      action-label="Save Payable Pay Code"
      :loading="loading"
      :stale="accrualQuoteStale"
      @back="visibleAccrualConfirmation = false"
      @confirm="confirmAccrualSave"
    />

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closePayCodeModal">Cancel</a-button>
        <a-button type="primary" :loading="loading" @click="submit">Save</a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import CustomSelect from '@/components/ui/custom-select.vue'
import { PAY_CODE_READY_FOR_PAY_STATUSES } from '@/store/modules/shifts/constants'
import { deepCopy } from '@/utils/deep-copy'
import { Button, FormModel, InputNumber, Modal, notification, Select, TimePicker } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import {
  formatScheduledStartForPayload,
  getScheduledStartForWorkDay,
  getWorkDayDateForShift,
  getWorkDayStartTime,
  isScheduledStartInWorkDay,
  normalizePayCodeTime,
  timePickerValue
} from '../../utils/pay-code-time'
import ShiftOverlapModal from '../modal/shift-overlap-modal.vue'
import PayCodeAccrualConfirmation from './shift-pay-code-accrual-confirmation.vue'

const { mapActions: shiftActions, mapState: shiftState } = createNamespacedHelpers('shifts')
const { mapActions: accrualActions } = createNamespacedHelpers('accrual')

export default {
  components: {
    'a-modal': Modal,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-input-number': InputNumber,
    'a-button': Button,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-time-picker': TimePicker,
    'custom-select': CustomSelect,
    'shift-overlap-modal': ShiftOverlapModal,
    'pay-code-accrual-confirmation': PayCodeAccrualConfirmation
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    shiftId: {
      type: Number,
      default: undefined
    }
  },
  data() {
    return {
      form: {
        ready_for_pay_status: undefined,
        pay_code: undefined,
        is_planned: null,
        time: undefined
      },
      rules: {
        pay_code: [
          {
            required: true,
            message: 'Please select pay code',
            trigger: 'change',
            type: 'object'
          }
        ],
        time: [
          {
            validator: this.validateTime,
            trigger: 'change'
          }
        ]
      },
      type: 'attached',

      loading: false,
      workDayDate: null,
      overlappingShifts: [],
      visibleOverlapModal: false,
      visibleAccrualConfirmation: false,
      accrualQuoteStale: false,
      accrualQuote: undefined,
      pendingPayload: undefined,

      readyForPayStatus: Object.entries(PAY_CODE_READY_FOR_PAY_STATUSES).map(([key, value]) => ({
        key,
        ...value
      }))
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal && this.shift?.pay_code) {
          this.form.pay_code = this.shift.pay_code
          this.form.ready_for_pay_status =
            this.shift.internal_payment_info.ready_for_pay_status === 'blocked' ? 'blocked' : 'unblocked'
          this.form.pay_code.value =
            this.shift.pay_code.type === 'total'
              ? this.shift.internal_payment_info.rate / 100
              : this.shift.hours.work_hours
          this.form.time = normalizePayCodeTime(this.shift.scheduled_start, this.workDayStart)
          this.workDayDate = getWorkDayDateForShift(this.shift.scheduled_start, this.workDayStart, this.timeZone)

          if (!this.shift.attached) {
            this.type = 'independent'
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    ...shiftState({
      shift: state => state.shift.shift,
      associate: state => state.shift.associate
    }),

    payCodes() {
      return this.$store.state.applicationState.projectPayCodes
    },

    availablePayCodes() {
      switch (this.type) {
        case 'attached':
          return this.payCodes.filter(item => item.attached)
        case 'independent':
          return this.payCodes.filter(item => !item.attached)
        default:
          return []
      }
    },

    payCodeProxy: {
      get() {
        return this.form.pay_code ? Number(this.form.pay_code.id) : undefined
      },
      set(value) {
        this.form.pay_code = this.payCodes.find(item => item.id === value)
      }
    },

    paymentSettings() {
      return this.$store.state.applicationState.currentProject?.payment_settings
    },

    timeZone() {
      return this.$store.state.applicationState.timeZone
    },

    workDayStart() {
      return getWorkDayStartTime(this.paymentSettings)
    },

    defaultTimeValue() {
      return timePickerValue(this.workDayStart)
    },

    timeValue() {
      return timePickerValue(this.form.time, this.workDayStart)
    },
    outsideBalanceRows() {
      if (!this.accrualQuote || Number(this.accrualQuote.outside) <= 0) return []
      const name = `${this.associate?.first_name || ''} ${this.associate?.last_name || ''}`.trim()
      return [
        {
          key: this.shiftId,
          associate_id: this.associate?.id,
          associate: name || this.associate?.associate_number || '-',
          quote: this.accrualQuote
        }
      ]
    }
  },
  methods: {
    ...shiftActions(['updatePayCode']),
    ...accrualActions(['quotePayCode']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    openOverlapShifts(shifts) {
      this.visibleOverlapModal = true
      this.overlappingShifts = deepCopy(shifts)
    },

    closePayCodeModal() {
      this.overlappingShifts = []
      this.visibleAccrualConfirmation = false
      this.accrualQuote = undefined
      this.pendingPayload = undefined
      this.$emit('update:visible', false)
    },

    handleTimeChange(time) {
      this.form.time = normalizePayCodeTime(time, this.workDayStart)
      this.$nextTick(() => {
        this.$refs.payCodeForm?.validateField('time')
      })
    },

    validateTime(rule, value, callback) {
      if (!value) {
        callback(new Error('Please select start time'))
        return
      }

      if (!this.workDayDate) {
        callback()
        return
      }

      const scheduledStart = getScheduledStartForWorkDay(this.workDayDate, value, this.workDayStart, this.timeZone)

      if (!isScheduledStartInWorkDay(scheduledStart, this.workDayDate, this.workDayStart, this.timeZone)) {
        callback(new Error('Start time must be within the current Work Day.'))
        return
      }

      callback()
    },

    isAccrualPayCode() {
      return this.form.pay_code?.type === 'duration' && Boolean(this.form.pay_code?.accrual_category)
    },

    async prepareAccrualSave(payload, stale = false) {
      this.accrualQuote = await this.quotePayCode({
        project_id: this.$store.state.applicationState.currentProject.id,
        associate_id: this.associate.id,
        pay_code_id: payload.pay_code.id,
        value: payload.pay_code.value,
        shift_id: payload.shift_id
      })
      this.pendingPayload = payload
      this.accrualQuoteStale = stale

      if (Number(this.accrualQuote.outside) > 0) {
        this.visibleAccrualConfirmation = true
        return
      }

      if (stale) {
        this.visibleAccrualConfirmation = false
        this.showNotification(
          'warning',
          'Balance Changed',
          'Paid-time balances changed. Review the updated amounts before creating Pay Codes.'
        )
        return
      }

      await this.savePayload(payload)
    },

    async savePayload(payload, reason = '') {
      const data = await this.updatePayCode({
        ...payload,
        outside_balance_confirmed: Boolean(reason),
        outside_balance_reason: reason,
        accrual_quote_revision: this.accrualQuote?.revision
      })
      if (data?.length) {
        this.openOverlapShifts(data)
        return
      }
      this.$emit('edit')
      this.closePayCodeModal()
    },

    async confirmAccrualSave(reason) {
      try {
        this.loading = true
        await this.savePayload(this.pendingPayload, reason)
      } catch (error) {
        if (this.isStaleQuote(error)) {
          await this.prepareAccrualSave(this.pendingPayload, true)
          return
        }
        this.showNotification('error', 'Error', error?.detail || error?.message || error)
      } finally {
        this.loading = false
      }
    },

    isStaleQuote(error) {
      return error?.code === 'accrual_stale_quote' || error?.code?.includes?.('accrual_stale_quote')
    },

    async submit() {
      try {
        const valid = await new Promise(resolve => {
          this.$refs.payCodeForm.validate(resolve)
        })

        if (!valid) {
          this.showNotification('warning', 'Validation Error', 'Please fill in all required fields correctly.')

          return
        }

        this.loading = true

        this.form.pay_code.is_planned = null

        const { ready_for_pay_status, time, ...rest } = this.form

        const workDayDate =
          this.workDayDate || getWorkDayDateForShift(this.shift.scheduled_start, this.workDayStart, this.timeZone)

        const payload = {
          ...rest,
          shift_id: this.shiftId,
          ready_for_pay_status: ready_for_pay_status,
          scheduled_start: formatScheduledStartForPayload(workDayDate, time, this.workDayStart, this.timeZone)
        }

        if (this.isAccrualPayCode()) {
          await this.prepareAccrualSave(payload)
        } else {
          await this.savePayload(payload)
        }
      } catch (error) {
        this.showNotification('error', 'Error', error?.detail || error?.message || error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

.ant-input-number {
  width: 100%;
}
</style>
